import { useEffect, useState, useSyncExternalStore } from 'react';
import { supabase } from './supabase';

// Ted's weekly sales list in a coaching group (Gary, 1 Nov 2026): one named lead, customer or property per task,
// grouped, each recorded done or not done with a reason. Shared by the chat's Tasks button and the Info panel's
// Tasks tab, so a change in one shows in the other. The database decides who may see it (ted_tasks_for_thread).

export type TaskGroup = 'leads' | 'customers' | 'pos' | 'other';
export type TaskStatus = 'open' | 'done' | 'not_done' | 'carried';

export interface TedTask {
  id: string;
  week_start: string;
  title: string;
  detail: string | null;
  due_date: string | null;
  kind: 'commitment' | 'task';
  status: TaskStatus;
  task_group: TaskGroup;
  reason: string | null;
  note: string | null;
  done_at: string | null;
  done_by_name: string | null;
  done_by_ted: boolean;
  carried: boolean;
  /** Who added it themselves (null: Ted did); mine: the person looking. */
  added_by_name: string | null;
  mine: boolean;
  weeks: string[] | null;
  /** The batch it sits in (Gary, 2 Nov 2026: a task is a batch of names); null for a batch or a single task. */
  parent_id: string | null;
}

export const TASK_GROUPS: { key: TaskGroup; title: string; goal: string; icon: string }[] = [
  { key: 'leads', title: 'Leads', goal: 'Book or close them', icon: '🎯' },
  { key: 'customers', title: 'Customer follow-ups', goal: 'Rebooks, referrals and reviews', icon: '🔁' },
  { key: 'pos', title: 'Peace of Sleep', goal: 'Renewals and hospitality', icon: '🛏️' },
  { key: 'other', title: 'Other strategies', goal: 'Doctors, partners and referrals', icon: '🤝' },
];

/** The choices when recording a task. Not done always needs one. */
export const OUTCOMES = {
  done: ['Booked', 'Quote sent', 'Call back booked', 'Renewed', 'Visited'],
  not_done: ['No answer', 'Call back later', 'Not interested', 'Booked elsewhere', 'Wrong number', 'Ran out of time', 'Other'],
} as const;

// One change anywhere reloads every list on screen.
let version = 0;
const listeners = new Set<() => void>();
export function tasksChanged() {
  version++;
  for (const l of listeners) l();
}
function useVersion(): number {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => version);
}

// Which tab the Info panel shows in a coaching group: Tasks first (Gary, 1 Nov).
export type InfoTab = 'tasks' | 'info';
let tab: InfoTab = 'tasks';
const tabListeners = new Set<() => void>();
export function setInfoTab(t: InfoTab) {
  tab = t;
  for (const l of tabListeners) l();
}
export function useInfoTab(): InfoTab {
  return useSyncExternalStore((l) => { tabListeners.add(l); return () => tabListeners.delete(l); }, () => tab);
}

export interface TedTasksState {
  /** null while loading; false when this is not a coaching group this person may see. */
  coaching: boolean | null;
  tasks: TedTask[];
  weeks: string[];
}

/** A coaching group's list for a week (this week when week is null). bump: reload when it changes (new messages). */
export function useTedTasks(threadId: string, week: string | null, bump = 0): TedTasksState {
  const v = useVersion();
  const [state, setState] = useState<TedTasksState>({ coaching: null, tasks: [], weeks: [] });
  useEffect(() => {
    if (!threadId) { setState({ coaching: false, tasks: [], weeks: [] }); return; }
    let gone = false;
    void (async () => {
      const [{ data, error }, visible] = await Promise.all([
        supabase.rpc('ted_tasks_for_thread', { p_thread_id: threadId, p_week: week }),
        supabase.rpc('ted_coaching_visible', { p_thread_id: threadId }),
      ]);
      if (gone) return;
      const rows = error ? [] : ((data ?? []) as TedTask[]);
      setState((s) => ({ coaching: visible.data === true, tasks: rows, weeks: rows[0]?.weeks?.length ? rows[0].weeks : s.weeks }));
    })();
    return () => { gone = true; };
  }, [threadId, week, bump, v]);
  return state;
}

/** The names that get ticked: items in a batch, and single tasks with nothing in them. Batches are not counted. */
export function leafTasks(tasks: TedTask[]): TedTask[] {
  const parents = new Set(tasks.map((t) => t.parent_id).filter((v): v is string => !!v));
  return tasks.filter((t) => !parents.has(t.id));
}

/** A name's due day: its own, else its batch's. */
export function dueOf(t: TedTask, tasks: TedTask[]): string | null {
  return t.due_date ?? tasks.find((b) => b.id === t.parent_id)?.due_date ?? null;
}

/** Record a task: done (optional outcome and note), not done (reason needed), or open again. Null when saved. */
export async function recordTask(id: string, status: 'open' | 'done' | 'not_done', reason: string | null, note: string | null): Promise<string | null> {
  const { error } = await supabase.rpc('ted_task_set', { p_task_id: id, p_status: status, p_reason: reason, p_note: note });
  if (error) return status === 'not_done' && !reason ? 'Say why it was not done' : 'Could not save that';
  tasksChanged();
  return null;
}

/** Add a task of their own to this week's list (Gary, 1 Nov 2026). Null when saved, else what to fix. */
export async function addTask(threadId: string, group: TaskGroup, title: string, due: string | null, note: string | null, parent: string | null = null): Promise<string | null> {
  const { error } = await supabase.rpc('ted_task_add', { p_thread_id: threadId, p_group: group, p_title: title, p_due: due, p_note: note, p_parent: parent });
  if (error) return /Name who|day this week|plenty|this week's list/.test(error.message) ? error.message : 'Could not add that';
  tasksChanged();
  return null;
}

/** Take a task they added off the list. Only their own. */
export async function removeTask(id: string): Promise<string | null> {
  const { error } = await supabase.rpc('ted_task_remove', { p_task_id: id });
  if (error) return 'Could not remove that';
  tasksChanged();
  return null;
}

/** Monday to Friday of this week, as YYYY-MM-DD, in the phone's own time (South Africa for our operators). */
export function weekdays(now = new Date()): string[] {
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return [0, 1, 2, 3, 4].map((i) => { const x = new Date(d); x.setUTCDate(d.getUTCDate() + i); return x.toISOString().slice(0, 10); });
}

/** "Tue 3 Nov" from a YYYY-MM-DD day. */
export function shortDay(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}
